import {
    Component,
    forwardRef,
    Input,
    OnInit,
    Output,
    EventEmitter,
    ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Item } from '@masar/common/models';
import { Observable } from 'rxjs';
import { ItemTreeCacheService } from './services/item-tree-cache.service';

@Component({
    selector: 'app-item-list-field',
    template: `
        <div class="flex w-full flex-col gap-3">
            @for (item of itemFields; track item; let idx = $index) {
                <div
                    class="group relative rounded-lg border border-slate-200 bg-white p-4 transition-all hover:border-slate-300 hover:shadow-sm"
                >
                    <!-- Item number indicator -->
                    @if (isMulti) {
                        <div
                            class="absolute top-3 flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700 ltr:left-3 rtl:right-3"
                        >
                            {{ idx + 1 }}
                        </div>
                    }
                    <div
                        class="flex flex-col gap-3 md:flex-row md:items-center"
                        [class.ltr:pl-8]="isMulti"
                        [class.rtl:pr-8]="isMulti"
                    >
                        <app-item-field
                            class="min-w-0 flex-1"
                            [maxNesting]="maxNesting"
                            [ngModel]="itemFields[idx].value"
                            (ngModelChange)="
                                itemFields[idx].value = $event; reportChange()
                            "
                            [disabled]="isDisabled"
                        >
                        </app-item-field>
                        @if (isMulti && !isDisabled) {
                            <button
                                (click)="removeField(idx)"
                                class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-400 transition-all hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
                                [attr.aria-label]="
                                    'translate_delete' | translate
                                "
                            >
                                <i class="fa-light fa-trash-can text-sm"></i>
                            </button>
                        }
                    </div>
                </div>
            }
        </div>

        @if (isMulti && !isDisabled) {
            <div class="mt-4 flex justify-center">
                <button
                    (click)="addNewField()"
                    class="inline-flex items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-2.5 text-sm font-medium text-slate-600 transition-all hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700"
                >
                    <i class="fa-light fa-plus"></i>
                    <span>{{ 'translate_add_item' | translate }}</span>
                </button>
            </div>
        }
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => ItemListFieldComponent),
        },
        ItemTreeCacheService,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ItemListFieldComponent implements OnInit, ControlValueAccessor {
    @Input() public parentFetcher: (
        childId: string
    ) => Observable<{ parent: Item; children: Item[] }>;
    @Input() public childrenFetcher: (parentId: string) => Observable<Item[]>;

    @Input() public maxNesting: number = -2;
    @Input() public isMulti: boolean = true;

    @Output() public valueChange = new EventEmitter<Item[] | Item>();

    public itemFields: {
        value?: Item;
    }[] = [];
    public isDisabled: boolean = false;

    private onChange: (items: Item[] | Item) => void;

    public constructor(private itemTreeCacheService: ItemTreeCacheService) {
        this.addNewField();
        this.reportChange();
    }

    public ngOnInit(): void {
        this.itemTreeCacheService.setParentFetcher(this.parentFetcher);
        this.itemTreeCacheService.setChildrenFetcher(this.childrenFetcher);
    }

    public writeValue(data: Item[] | Item): void {
        if (data instanceof Array) {
            const source = data || [];
            this.itemFields = source
                .filter(x => x?.id)
                .map(x => ({ value: x }));
        } else if (data?.id) {
            this.itemFields = [{ value: data }];
        } else {
            this.itemFields = [];
        }

        if (!this.itemFields || this.itemFields.length === 0) {
            this.addNewField();
        }
    }

    public registerOnChange(fn: (data: Item[] | Item) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(): void {
        return undefined;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    public addNewField(): void {
        this.itemFields.push({ value: null });
    }

    public removeField(index: number): void {
        if (this.itemFields.length === 1) {
            return;
        }
        this.itemFields.splice(index, 1);
        this.reportChange();
    }

    public reportChange(): void {
        const data = this.isMulti
            ? this.itemFields.filter(x => x.value?.id).map(x => x.value)
            : this.itemFields[0]?.value?.id
              ? this.itemFields[0].value
              : null;
        this.valueChange.emit(data);
        if (this.onChange) {
            this.onChange(data);
        }
    }
}
